import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { Search, Loader2, Plus, Building2, UserRound, Check } from "lucide-react";

/**
 * Smart CRM-style autocomplete used by the Add-Funding and Edit-Loan flows.
 *
 * Props:
 *   kind: "attorney" | "firm"
 *   value: current display value (email for attorneys, firm name for firms)
 *   onSelect(record): called when user picks a row
 *   onCreateNew(text): optional — if provided, surfaces an "Add new" footer row
 *   placeholder, testidPrefix
 */
export default function EntitySearchSelect({
  kind = "attorney",
  value = "",
  onSelect,
  onCreateNew,
  placeholder,
  testidPrefix = "ess",
}) {
  const [text, setText] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { setText(value || ""); }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const search = async (q) => {
    setLoading(true);
    try {
      const endpoint = kind === "firm" ? "/operations/firms/search" : "/operations/attorneys/search";
      const { data } = await api.get(endpoint, { params: { q, limit: 8 } });
      setResults(Array.isArray(data) ? data : []);
    } catch { setResults([]); }
    finally { setLoading(false); }
  };

  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    setOpen(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 200);
  };

  const onFocus = () => {
    setOpen(true);
    if (!results.length) search(text || "");
  };

  const pick = (row) => {
    setOpen(false);
    if (kind === "firm") {
      setText(row.name || "");
    } else {
      setText(row.email || "");
    }
    onSelect && onSelect(row);
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
        <input
          value={text}
          onChange={onChange}
          onFocus={onFocus}
          placeholder={placeholder || (kind === "firm" ? "Search firms…" : "Search attorneys by email or name…")}
          className="w-full h-10 pl-9 pr-3 rounded-md border border-[#E5E7EB] text-sm focus:outline-none focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]"
          data-testid={`${testidPrefix}-input`}
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF] animate-spin" />}
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[#E5E7EB] rounded-md shadow-lg max-h-80 overflow-y-auto" data-testid={`${testidPrefix}-dropdown`}>
          {results.length === 0 && !loading && (
            <div className="px-3 py-2.5 text-xs text-[#6B7280]">No matches. {onCreateNew ? "Use ‘Add new’ below." : ""}</div>
          )}
          {results.map((r, i) => kind === "firm" ? (
            <button type="button" key={r.firm_id} onClick={() => pick(r)} className="w-full text-left px-3 py-2.5 hover:bg-[#F0F6FF] flex items-center justify-between gap-2 border-b border-[#F3F4F6] last:border-b-0" data-testid={`${testidPrefix}-row-${i}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                <Building2 className="w-3.5 h-3.5 text-[#0066FF] flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#0A2540] truncate">{r.name}</div>
                  <div className="text-[11px] text-[#6B7280] truncate">{r.primary_contact_email || (r.domains || []).join(", ") || "—"}</div>
                </div>
              </div>
              <div className="text-[11px] text-[#9CA3AF] tabular-nums flex-shrink-0">{r.loan_count} loan{r.loan_count !== 1 ? "s" : ""}</div>
            </button>
          ) : (
            <button type="button" key={r.email} onClick={() => pick(r)} className="w-full text-left px-3 py-2.5 hover:bg-[#F0F6FF] flex items-center justify-between gap-2 border-b border-[#F3F4F6] last:border-b-0" data-testid={`${testidPrefix}-row-${i}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                <UserRound className="w-3.5 h-3.5 text-[#0066FF] flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#0A2540] truncate">{r.name || r.email}</div>
                  <div className="text-[11px] text-[#6B7280] truncate">{r.email}{r.firm ? ` · ${r.firm}` : ""}</div>
                </div>
              </div>
              <div className="text-[11px] text-[#9CA3AF] tabular-nums flex-shrink-0">{r.loan_count} loan{r.loan_count !== 1 ? "s" : ""}</div>
            </button>
          ))}
          {onCreateNew && text.trim() && !results.find((r) => (kind === "firm" ? r.name : r.email)?.toLowerCase() === text.trim().toLowerCase()) && (
            <button type="button" onClick={() => { setOpen(false); onCreateNew(text.trim()); }}
              className="w-full text-left px-3 py-2.5 bg-[#F0F6FF] hover:bg-[#DBEAFE] flex items-center gap-2 text-sm font-medium text-[#1D4ED8]" data-testid={`${testidPrefix}-create`}>
              <Plus className="w-3.5 h-3.5" /> Use new {kind}: <span className="truncate">{text.trim()}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
